import { useMutation, useQueryClient } from 'react-query'
import {
  CrunchData,
  DocumentSvcCrunches,
} from '@dc-shared-web/hooks/api/document/types'
import { useSvcCrunchesContext } from '@dc-shared-web/providers/SvcCrunchesProvider'
import { updateProject } from '@dc-shared-web/hooks/api/project/updateProject'
import { useToast } from '@dc-shared-web/components/shadcn/use-toast'

export type UpdateProjectParams = {
  field: string
  value: string | number | null
  id: string | number | null | undefined
}

export const useUpdateProject = (params: {
  crunchUUID?: string
  projectUUID?: string
  onSuccess?: () => void
  onError?: () => void
}) => {
  const apiClient = useSvcCrunchesContext()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation(
    (mutationParams: UpdateProjectParams) =>
      updateProject(apiClient, mutationParams),
    {
      onSuccess: async () => {
        await Promise.allSettled([
          params.crunchUUID
            ? queryClient.invalidateQueries<CrunchData[]>({
                queryKey: ['Document', params.crunchUUID],
              })
            : Promise.resolve(),
          queryClient.invalidateQueries<DocumentSvcCrunches>({
            queryKey: ['DocumentSvcCrunches', params.crunchUUID],
          }),
          queryClient.invalidateQueries({
            queryKey: ['projects'],
          }),
          queryClient.invalidateQueries({
            queryKey: ['folders', 'list'],
          }),
          queryClient.invalidateQueries(['folder']),
          params.projectUUID
            ? queryClient.invalidateQueries({
                queryKey: ['project', params.projectUUID],
              })
            : Promise.resolve(),
        ])

        params.onSuccess?.()
      },
      onError: () => {
        toast({
          variant: 'error',
          title: 'Error updating project',
          description: 'If the issue persists, please contact support.',
        })
      },
    }
  )
}
